import React, { useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { useFarms, useDashboardLocal, usePredictedBirths, useDispatches } from "@/lib/repo";
import { KpiCard } from "@/components/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ChevronRight, ScanLine, Sprout, Bird, Activity, MapPin, Zap, Boxes, Baby, Truck } from "lucide-react";
import { formatDateTime } from "@/lib/utils";

const CAT_LABELS = {
  vaca: "Vacas", ternera: "Becerras", ternero: "Becerros",
  toro: "Toros", vaquillona: "Novillas", novillo: "Mautes", mauta: "Mautas",
  bufala: "Búfalas", novilla_bufalina: "Novillas bufalinas", becerra_bufalina: "Becerras bufalinas",
  bufalo: "Búfalos", maute_bufalino: "Mautes bufalinos", becerro_bufalino: "Becerros bufalinos",
  mauta_bufalina: "Mautas bufalinas",
  sin_categoria: "Sin categoría",
};

// Eucalyptus / Ochre / Terracotta palette for categories
const CAT_COLORS = {
  vaca:          "hsl(138 24% 31%)",
  vaquillona:    "hsl(138 18% 50%)",
  ternera:       "hsl(42 60% 44%)",
  ternero:       "hsl(42 60% 60%)",
  toro:          "hsl(18 46% 52%)",
  novillo:       "hsl(205 38% 38%)",
  // Fix A (2026-08-24): 'Mauta' — hembra joven bovina entre becerra y novilla.
  mauta:         "hsl(190 40% 44%)",
  bufala:            "hsl(280 40% 35%)",
  novilla_bufalina:  "hsl(280 35% 50%)",
  becerra_bufalina:  "hsl(280 45% 65%)",
  bufalo:            "hsl(260 35% 28%)",
  maute_bufalino:    "hsl(265 40% 45%)",
  becerro_bufalino:  "hsl(270 45% 60%)",
  // Fix A (2026-08-24): equivalente hembra bufalina, por simetría con 'mauta'.
  mauta_bufalina:    "hsl(275 38% 55%)",
  sin_categoria: "hsl(36 14% 60%)",
};

export default function Dashboard() {
  const { user } = useAuth();
  const farms = useFarms();
  const [farmId, setFarmId] = useState("all");
  const data = useDashboardLocal(farmId === "all" ? null : farmId, user?.role === "handler" ? user?.assigned_farm_ids : undefined);
  const predictedBirths90 = usePredictedBirths({ days: 90 });
  const monthStart = useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10), []);
  const dispatchesThisMonth = useDispatches({ assignedFarmIds: user?.role === "handler" ? user?.assigned_farm_ids : undefined, dateFrom: monthStart });
  const dispatchedAnimalsThisMonth = useMemo(() => dispatchesThisMonth.reduce((a, d) => a + d.animal_count, 0), [dispatchesThisMonth]);
  const loading = false;

  const pieData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.totals.by_category)
      .filter(([_, v]) => v > 0)
      .map(([k, v]) => ({ name: CAT_LABELS[k] || k, key: k, value: v }));
  }, [data]);

  const today = useMemo(() => new Date().toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" }), []);

  const totalCalves = (data?.totals?.by_category?.ternero ?? 0) + (data?.totals?.by_category?.ternera ?? 0);

  return (
    <div className="space-y-6">
      {/* Editorial header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
            {today}
          </div>
          <h1 className="font-editorial text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-foreground mt-1">
            Hola, <span className="italic">{user?.name?.split(" ")[0] || ""}</span>.
          </h1>
          <p className="mt-1.5 text-[14px] text-muted-foreground">
            Resumen general de tu hacienda.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {farms.length > 0 && (
            <Select value={farmId} onValueChange={setFarmId}>
              <SelectTrigger className="h-10 w-44" data-testid="dashboard-farm-select">
                <SelectValue placeholder="Todas las fincas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fincas</SelectItem>
                {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button asChild variant="outline" size="default" data-testid="dashboard-initial-inventory">
            <Link to="/inventario-inicial"><Boxes size={16}/> Inv. inicial</Link>
          </Button>
          {user?.role !== "handler" && (
            <Button asChild variant="outline" size="default" data-testid="dashboard-actions-btn">
              <Link to="/acciones"><Zap size={16}/> Acciones</Link>
            </Button>
          )}
          <Button asChild size="default" data-testid="dashboard-new-inventory">
            <Link to="/inventario/nuevo"><ScanLine size={16}/> Inventario</Link>
          </Button>
        </div>
      </div>

      {/* KPIs (editorial: serif numbers) */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        <KpiCard testId="kpi-animals-active" tone="default" icon={Bird}
          label="Animales activos"
          value={loading ? "…" : (data?.totals?.animals_active ?? 0).toLocaleString("es-AR")}
          meta={data?.totals?.animals_active ? `${data.totals.animals_active} cabezas en producción` : "—"} />
        <KpiCard testId="kpi-farms" tone="warm" icon={MapPin}
          label="Fincas"
          value={loading ? "…" : (data?.totals?.farms ?? 0)}
          meta="establecimientos" />
        <KpiCard testId="kpi-vacas" tone="neutral" icon={Sprout}
          label="Vacas"
          value={loading ? "…" : (data?.totals?.by_category?.vaca ?? 0)}
          meta="reproductoras" />
        <KpiCard testId="kpi-terneros" tone="ochre" icon={Activity}
          label="Becerros + Becerras"
          value={loading ? "…" : totalCalves}
          meta="menores a 12 meses" />
        <Link to="/nacimientos-previstos" data-testid="kpi-predicted-births" className="block">
          <KpiCard tone="ochre" icon={Baby}
            label="Nacimientos previstos"
            value={loading ? "…" : predictedBirths90.length}
            meta="próximos 90 días" />
        </Link>
        <Link to="/despachos" data-testid="kpi-dispatches" className="block">
          <KpiCard tone="warm" icon={Truck}
            label="Salidos este mes"
            value={loading ? "…" : dispatchedAnimalsThisMonth}
            meta="ventas y despachos" />
        </Link>
      </div>

      {/* Acciones rápidas — Fix P: no se muestran al encargado */}
      {user?.role !== "handler" && (
      <div data-testid="dashboard-quick-actions">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <Zap size={15} className="text-primary" />
            <span className="font-heading font-semibold text-[13px] uppercase tracking-[0.05em]">Acciones rápidas</span>
          </div>
          <Link to="/acciones" className="text-[12px] text-primary font-medium hover:underline underline-offset-4" data-testid="dashboard-see-actions">Ver todas</Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
          {[{id:1,ico:"🔆➕",t:"Nacimiento",cat:"ingreso"},{id:10,ico:"🐄🔻",t:"Aborto",cat:"evento"},{id:12,ico:"🥛🐄",t:"Ingreso ordeño",cat:"evento"},{id:14,ico:"🐮➡️",t:"Salida ordeño",cat:"evento"},{id:3,ico:"➖☠️",t:"Muerte",cat:"egreso"},{id:4,ico:"🔪🥩",t:"Sacrificio",cat:"egreso"}].map((q) => (
            <Link key={q.id} to={q.id===1?"/animales/nacimiento":`/acciones/${q.id}`} data-testid={`dashboard-quick-${q.id}`}
              className="flex flex-col items-center gap-1.5 rounded-[var(--radius)] border border-border bg-card p-3 shadow-paper transition-transform duration-100 active:translate-y-[2px] hover:border-foreground/20">
              <span className="inline-flex h-11 w-auto min-w-[44px] px-2 rounded-[calc(var(--radius)-0.4rem)] items-center justify-center text-[18px] leading-none whitespace-nowrap" style={{ background: `hsl(var(--c-${q.cat}) / 0.16)`, border: `2px solid hsl(var(--c-${q.cat}) / 0.4)` }}>
                <span role="img" aria-label={q.t}>{q.ico}</span>
              </span>
              <span className="text-[11px] font-heading font-semibold text-foreground text-center leading-tight">{q.t}</span>
            </Link>
          ))}
        </div>
      </div>
      )}

      {/* Composition + Recent inventories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Composición</div>
              <CardTitle className="text-[17px] mt-0.5">Distribución por categoría</CardTitle>
            </div>
            <Badge variant="outline" className="text-[10.5px] font-mono">{pieData.length} categorías</Badge>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-14">
                <Sprout size={28} className="text-muted-foreground/60 mx-auto mb-2" />
                <p>Todavía no hay animales cargados.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr] gap-4 items-center">
                <div className="h-64" data-testid="dashboard-donut-chart">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={94} paddingAngle={2} stroke="hsl(var(--background))" strokeWidth={2}>
                        {pieData.map((entry) => <Cell key={entry.key} fill={CAT_COLORS[entry.key] || CAT_COLORS.sin_categoria} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          fontSize: 12,
                          color: "hsl(var(--foreground))",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-2.5">
                  {pieData.map((d) => (
                    <li key={d.key} className="flex items-center justify-between gap-3 text-[13px]">
                      <span className="flex items-center gap-2 text-foreground/90">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CAT_COLORS[d.key] || CAT_COLORS.sin_categoria }} />
                        {d.name}
                      </span>
                      <span className="font-mono tabular-nums text-foreground/80">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Sesiones</div>
              <CardTitle className="text-[17px] mt-0.5">Últimos inventarios</CardTitle>
            </div>
            <Link to="/inventario" className="text-[12px] text-primary font-medium hover:underline underline-offset-4" data-testid="dashboard-see-inventories">
              Ver todos
            </Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {(!data?.last_inventories || data.last_inventories.length === 0) && (
              <p className="text-sm text-muted-foreground py-4">Sin inventarios todavía.</p>
            )}
            {data?.last_inventories?.map((inv) => (
              <Link
                key={inv.id}
                to={`/inventario/${inv.id}`}
                className="flex items-center justify-between gap-2 p-3 rounded-[calc(var(--radius)-0.35rem)] hover:bg-muted transition-colors duration-150 ring-focus"
                data-testid="dashboard-inventory-row"
              >
                <div className="min-w-0">
                  <div className="text-[14px] font-heading font-semibold text-foreground truncate tracking-tight">{inv.name || "Inventario"}</div>
                  <div className="text-[10.5px] font-mono text-muted-foreground tabular-nums mt-0.5">{formatDateTime(inv.started_at)}</div>
                  <div className="text-[11.5px] mt-1.5 flex items-center gap-1.5">
                    <Badge variant="present" className="font-mono tabular-nums">{inv.scanned_animal_ids?.length || 0} pres</Badge>
                    <Badge variant="missing" className="font-mono tabular-nums">{inv.missing_animal_ids?.length || 0} falt</Badge>
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground/60 shrink-0" />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
